'use client';

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import FilterSection from './FilterSection';
import AgentCard from './AgentCard';
import Icon from '@/components/ui/AppIcon';
import { getStorageUrl } from '@/lib/api';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api';

interface FilterState {
  searchQuery: string;
  selectedArea: string;
  selectedAreaType: string;
  selectedService: string;
}

interface ServiceTag {
  id: number;
  name: string;
  slug: string;
}

interface Agent {
  id: number;
  slug: string;
  name: string;
  area: string;
  image: string | null;
  alt: string;
  rating: number;
  reviewCount: number;
  phone: string;
  completedOrders: number;
  experienceYears: number;
  services: ServiceTag[];
  availability?: string;
}

interface ApiAgent {
  id: number;
  slug: string;
  name: string;
  area: string;
  image: string | null;
  rating: number;
  reviewCount: number;
  completedOrders: number;
  experienceYears: number;
  phone: string;
  memberSince: string;
  services: ServiceTag[];
  availability?: string;
}

const AgentDirectoryInteractive = () => {
  const searchParams = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>([]);
  const [sortBy, setSortBy] = useState('rating');
  const [initialArea, setInitialArea] = useState('');
  const [initialAreaType, setInitialAreaType] = useState('');

  const transformApiAgent = (apiAgent: ApiAgent): Agent => ({
    id: apiAgent.id,
    slug: apiAgent.slug,
    name: apiAgent.name,
    area: apiAgent.area,
    image: getStorageUrl(apiAgent.image),
    alt: `${apiAgent.name} - Agent Surilipat di ${apiAgent.area}`,
    rating: apiAgent.rating,
    reviewCount: apiAgent.reviewCount,
    phone: apiAgent.phone,
    completedOrders: apiAgent.completedOrders,
    experienceYears: apiAgent.experienceYears,
    services: apiAgent.services,
    availability: apiAgent.availability,
  });

  const fetchAgents = useCallback(async (area?: string, search?: string, sort?: string) => {
    try {
      const params = new URLSearchParams();
      if (area) params.append('area', area);
      if (search) params.append('search', search);
      if (sort) params.append('sort', sort);

      const response = await fetch(`${API_URL}/agents?${params.toString()}`);
      const data = await response.json();

      if (data.agents) {
        const transformedAgents = data.agents.map(transformApiAgent);
        setAgents(transformedAgents);
        setFilteredAgents(transformedAgents);
      }
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      setAgents([]);
      setFilteredAgents([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setIsHydrated(true);

    // Read initial area from URL query params
    const areaParam = searchParams.get('area') || '';
    const typeParam = searchParams.get('type') || '';

    setInitialArea(areaParam);
    setInitialAreaType(typeParam);

    // Fetch agents from API
    fetchAgents(areaParam, '', sortBy);
  }, [searchParams, fetchAgents, sortBy]);

  const handleFilterChange = (filters: FilterState) => {
    // Filter locally from fetched agents
    let filtered = [...agents];

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (agent) =>
          agent.name.toLowerCase().includes(query) || agent.area.toLowerCase().includes(query)
      );
    }

    if (filters.selectedArea) {
      const areaLower = filters.selectedArea.toLowerCase();
      filtered = filtered.filter((agent) => {
        const agentAreaLower = agent.area.toLowerCase();
        return agentAreaLower.includes(areaLower) || areaLower.includes(agentAreaLower);
      });
    }

    if (filters.selectedService) {
      const serviceLower = filters.selectedService.toLowerCase();
      filtered = filtered.filter((agent) =>
        agent.services.some((service) => service.name.toLowerCase() === serviceLower)
      );
    }

    setFilteredAgents(filtered);
  };

  const handleSortChange = (sortOption: string) => {
    setSortBy(sortOption);
    let sorted = [...filteredAgents];

    switch (sortOption) {
      case 'rating':
        sorted.sort((a, b) => b.rating - a.rating);
        break;
      case 'reviews':
        sorted.sort((a, b) => b.reviewCount - a.reviewCount);
        break;
      case 'orders':
        sorted.sort((a, b) => b.completedOrders - a.completedOrders);
        break;
      default:
        break;
    }

    setFilteredAgents(sorted);
  };

  if (!isHydrated || isLoading) {
    return (
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="animate-pulse -mt-10 relative z-10">
          <div className="h-20 bg-card rounded-2xl shadow-xl"></div>
        </div>
        <div className="py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-[480px] bg-muted rounded-[2.5rem]"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1280px] mx-auto px-6">
      {/* Filter Section - positioned to overlap with hero */}
      <div className="-mt-10 relative z-10">
        <FilterSection
          onFilterChange={handleFilterChange}
          initialArea={initialArea}
          initialAreaType={initialAreaType}
        />
      </div>

      <div className="flex items-center justify-between mt-4 mb-8">
        <div>
          <h2 className="text-2xl font-heading font-black text-textPrimary flex items-center gap-3">
            <span className="w-2 h-8 bg-primary rounded-full"></span>
            {filteredAgents.length} Agent Dijumpai
          </h2>
          <p className="text-sm font-body text-textSecondary ml-5 mt-1">
            Menunjukkan agent terbaik di sekitar anda
          </p>
        </div>
        <div className="flex items-center gap-3 bg-card px-4 py-2 rounded-xl border border-border">
          <span className="text-xs font-bold text-textSecondary uppercase tracking-wider">
            Susun:
          </span>
          <select
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
            className="text-sm font-extrabold border-none bg-transparent text-textPrimary p-0 focus:ring-0 cursor-pointer"
          >
            <option value="rating">Penilaian Tertinggi</option>
            <option value="reviews">Ulasan Terbanyak</option>
            <option value="orders">Pesanan Terbanyak</option>
          </select>
        </div>
      </div>

      {filteredAgents.length === 0 ? (
        <div className="text-center py-16">
          <Icon
            name="MagnifyingGlassIcon"
            size={64}
            variant="outline"
            className="mx-auto text-textTertiary mb-4"
          />
          <h3 className="text-xl font-heading font-bold text-textPrimary mb-2">
            Tiada Agent Dijumpai
          </h3>
          <p className="text-textSecondary">
            Cuba ubah kriteria pencarian anda atau kosongkan tapis untuk melihat semua agent.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAgents.map((agent) => (
            <AgentCard key={agent.id} agent={agent} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentDirectoryInteractive;
